// "Frame by frame": the sampled frames on the video's timeline, with a
// marker wherever the AI reviewer pinned a finding. Choosing a marker, a
// thumbnail, or a timestamp anywhere in the result shows that frame and
// everything noted at it. A filter narrows the markers, notes and frames
// to one kind of finding, e.g. only the frames with issues.
import React, { forwardRef, useMemo, useState } from 'react';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import {
    FRAME_FILTERS, buildEvents, filterMoments, frameTime, groupMoments, keepsEvent, nearestFrameIndex, pickStartMoment, timelineLength, worstTone,
} from './qcTimeline';
import { AI } from './aiUi';
import Filmstrip, { Lightbox } from './Filmstrip';
import FrameFilter from './FrameFilter';
import TimelineTrack from './TimelineTrack';
import { Button, SectionTitle, ToneMark } from './ui';

const SIZES = {
    portrait: { preview: { width: 168, height: 299 }, thumb: { width: 34, height: 60 } },
    wide: { preview: { width: 320, height: 180 }, thumb: { width: 64, height: 36 } },
    square: { preview: { width: 220, height: 220 }, thumb: { width: 46, height: 46 } },
};

// format_meta.aspect_ratio ("9:16", "16:9", "1:1"); vertical when unknown.
export function shapeOf(aspect) {
    const m = /^\s*(\d+(?:\.\d+)?)\s*[:x/]\s*(\d+(?:\.\d+)?)\s*$/.exec(typeof aspect === 'string' ? aspect : '');
    if (!m || !Number(m[2])) return 'portrait';
    const ratio = Number(m[1]) / Number(m[2]);
    if (ratio > 1.1) return 'wide';
    return ratio < 0.9 ? 'portrait' : 'square';
}

const KIND_LABEL = { result: 'Checklist', outlier: 'Flagged', feedback: 'Frame note' };
const DARK_INK = { bad: '#fca5a5', flag: '#c4b5fd', warn: '#fcd34d', good: '#86efac' };
// Secondary text on the navy panel (AA on every stop of its gradient).
const DIM = 'rgba(255,255,255,0.72)';
// The panel's ghost buttons: Previous / Next note, Show all.
const GHOST = { background: 'rgba(255,255,255,0.1)' };

function Preview({ frame, size, onOpen }) {
    if (!frame) {
        return (
            <div style={{ ...size, flexShrink: 0, borderRadius: 12, background: 'rgba(255,255,255,0.06)', border: '1px dashed rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.7)', fontSize: 12, textAlign: 'center', padding: 12, boxSizing: 'border-box' }}>
                No frame for this moment
            </div>
        );
    }
    const chip = { position: 'absolute', background: 'rgba(17,25,54,0.78)', color: '#fff', borderRadius: 7, display: 'inline-flex', alignItems: 'center' };
    return (
        <button type="button" className="vqc-focus" onClick={onOpen} aria-label={`Enlarge frame ${frame.ts}`}
            style={{ position: 'relative', ...size, flexShrink: 0, padding: 0, border: '1px solid rgba(255,255,255,0.16)', borderRadius: 12, overflow: 'hidden', background: '#0b1029', cursor: 'zoom-in' }}>
            <img src={frame.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
            <span style={{ ...chip, left: 8, top: 8, fontSize: 12, fontWeight: 700, padding: '3px 7px' }}>{frame.ts}</span>
            <span style={{ ...chip, right: 8, bottom: 8, padding: 5 }}><OpenInFullRoundedIcon style={{ fontSize: 14 }} /></span>
        </button>
    );
}

function EventRow({ event }) {
    const meta = [KIND_LABEL[event.kind], event.kind === 'outlier' ? event.severity : event.label, event.critical ? 'Critical' : null];
    return (
        <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <span style={{ marginTop: 5 }}><ToneMark tone={event.tone} flag={event.kind === 'outlier'} /></span>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: DARK_INK[event.tone] }}>{meta.filter(Boolean).join(' · ')}</div>
                {event.note && <div style={{ fontSize: 13.5, color: '#fff', marginTop: 2, lineHeight: 1.45 }}>{event.note}</div>}
            </div>
        </li>
    );
}

// Every moment with notes, as a list: an index of the timeline that also
// works without a mouse. The current one is highlighted.
function MomentIndex({ title, moments, isCurrent, onSelect }) {
    if (moments.length < 2) return null;
    return (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: DIM, marginBottom: 6 }}>{title} ({moments.length})</div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 168, overflowY: 'auto' }}>
                {moments.map((m) => {
                    const current = isCurrent(m);
                    const lead = m.events[0];
                    return (
                        <li key={m.t}>
                            <button type="button" className="vqc-focus" aria-current={current ? 'true' : undefined} onClick={() => onSelect(m.t)}
                                style={{ width: '100%', minHeight: 32, display: 'flex', alignItems: 'center', gap: 9, padding: '6px 8px', border: 'none', borderRadius: 7, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', background: current ? 'rgba(254,189,28,0.14)' : 'transparent', color: '#fff' }}>
                                <ToneMark tone={m.tone} flag={m.tone === 'flag'} size={8} />
                                <span style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', width: 36, flexShrink: 0 }}>{m.ts}</span>
                                <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.82)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{lead.note || lead.label}</span>
                                {m.events.length > 1 && <span style={{ fontSize: 12, color: DIM, flexShrink: 0 }}>+{m.events.length - 1}</span>}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

// The filter state and what each filter keeps. With frames, a filter keeps
// the frames that carry at least one of its notes; without, its moments.
function useFrameFilter(frames, moments, frameEvents) {
    const [filterId, setFilterId] = useState('all');
    const keptFrames = useMemo(() => new Map(FRAME_FILTERS.map((f) => [f.id, frames.map((_, i) => i)
        .filter((i) => !f.tones || (frameEvents.get(i) || []).some((e) => keepsEvent(f, e)))])), [frames, frameEvents]);
    const filters = useMemo(() => FRAME_FILTERS.map((f) => ({
        ...f, count: frames.length ? keptFrames.get(f.id).length : filterMoments(moments, f).length,
    })), [frames, moments, keptFrames]);
    const filter = FRAME_FILTERS.find((f) => f.id === filterId);
    const shownMoments = useMemo(() => filterMoments(moments, filter), [moments, filter]);
    return { filterId, setFilterId, filter, filters, keptFrames, shownMoments, shownFrames: keptFrames.get(filterId) };
}

const VideoTimeline = forwardRef(function VideoTimeline({ video, check, selectedT = null, onSelect }, ref) {
    const [enlarged, setEnlarged] = useState(null);
    const frames = useMemo(() => (Array.isArray(video?.frames) ? video.frames.filter((f) => f && typeof f.url === 'string') : []), [video]);
    const events = useMemo(() => buildEvents(check), [check]);
    const moments = useMemo(() => groupMoments(events), [events]);
    const frameOf = useMemo(() => new Map(events.map((e) => [e.id, nearestFrameIndex(frames, e.t)])), [events, frames]);
    const frameEvents = useMemo(() => events.reduce((map, e) => {
        const i = frameOf.get(e.id);
        return i < 0 ? map : new Map(map).set(i, [...(map.get(i) || []), e]);
    }, new Map()), [events, frameOf]);
    const frameTones = useMemo(() => new Map([...frameEvents].map(([i, list]) => [i, worstTone(list)])), [frameEvents]);
    const { filterId, setFilterId, filter, filters, keptFrames, shownMoments, shownFrames } = useFrameFilter(frames, moments, frameEvents);

    if (!frames.length && !moments.length) return null;

    const length = timelineLength(video, frames, events);
    const t = selectedT ?? pickStartMoment(moments)?.t ?? frameTime(frames[0]) ?? 0;
    const index = nearestFrameIndex(frames, t);
    const frame = index >= 0 ? frames[index] : null;
    const shape = SIZES[shapeOf(video?.format_meta?.aspect_ratio)];

    // With frames, a moment belongs to its nearest frame; without, to its own time.
    const posOf = (time) => (frame ? nearestFrameIndex(frames, time) : time);
    const cur = frame ? index : t;
    const here = events.filter((e) => (frame ? frameOf.get(e.id) === index : e.t === t));
    const selectedTimes = new Set(shownMoments.filter((m) => posOf(m.t) === cur).map((m) => m.t));
    const prev = [...shownMoments].reverse().find((m) => posOf(m.t) < cur);
    const next = shownMoments.find((m) => posOf(m.t) > cur);
    const shownTs = frame ? frame.ts : (moments.find((m) => m.t === t)?.ts || '');

    // A filter that hides the current frame jumps to the first thing it keeps.
    const choose = (id) => {
        const kept = filterMoments(moments, FRAME_FILTERS.find((f) => f.id === id));
        const stays = frame ? keptFrames.get(id).includes(index) : kept.some((m) => m.t === t);
        setFilterId(id);
        if (!stays && kept.length) onSelect(pickStartMoment(kept).t);
    };

    const hint = `${frames.length} frames sampled · ${moments.length} ${moments.length === 1 ? 'moment' : 'moments'} with notes. Pick a marker or a frame to see what the reviewer saw.`;

    return (
        <div ref={ref} role="group" aria-label="Frame by frame" style={{ scrollMarginTop: 24 }}>
            <SectionTitle hint={hint} aside={<FrameFilter options={filters} value={filterId} unit={frames.length ? 'frame' : 'moment'} onChange={choose} />}>
                Frame by frame
            </SectionTitle>
            <div className="vqc-dark" style={{ background: AI.panel, borderRadius: 16, padding: 18, color: '#fff', display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 18px 40px -24px rgba(17,25,54,0.8)' }}>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <Preview frame={frame} size={shape.preview} onOpen={() => setEnlarged(frame)} />
                    <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                            <div>
                                <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.3, fontVariantNumeric: 'tabular-nums' }}>{shownTs}</div>
                                {frame && <div style={{ fontSize: 12, color: DIM }}>Frame {index + 1} of {frames.length}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <Button small variant="navy" disabled={!prev} onClick={() => onSelect(prev.t)} style={GHOST}>
                                    <ChevronLeftRoundedIcon aria-hidden="true" style={{ fontSize: 16 }} />Previous note
                                </Button>
                                <Button small variant="navy" disabled={!next} onClick={() => onSelect(next.t)} style={GHOST}>
                                    Next note<ChevronRightRoundedIcon aria-hidden="true" style={{ fontSize: 16 }} />
                                </Button>
                            </div>
                        </div>
                        <div aria-live="polite" style={{ flex: 1 }}>
                            {here.length ? (
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {here.map((e) => <EventRow key={e.id} event={e} />)}
                                </ul>
                            ) : (
                                <p style={{ margin: 0, fontSize: 13, color: DIM }}>Nothing noted at this frame. Use the markers to jump to what the reviewer found.</p>
                            )}
                        </div>
                        <MomentIndex title={filter.tones ? filter.label : 'All notes'} moments={shownMoments} isCurrent={(m) => selectedTimes.has(m.t)} onSelect={onSelect} />
                    </div>
                </div>
                <TimelineTrack length={length} moments={shownMoments} selectedTimes={selectedTimes} currentT={frame ? frameTime(frame) ?? t : t} onSelect={onSelect} />
                {filter.tones && frames.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, fontSize: 12, color: DIM, marginBottom: -8 }}>
                        Showing {shownFrames.length} of {frames.length} frames: {filter.label.toLowerCase()}
                        <Button small variant="navy" onClick={() => choose('all')} style={GHOST}>Show all</Button>
                    </div>
                )}
                {frames.length > 0 && (
                    <Filmstrip frames={frames} shown={shownFrames} selectedIndex={index} frameTones={frameTones} size={shape.thumb}
                        onSelect={(i) => onSelect(frameTime(frames[i]) ?? 0)} />
                )}
            </div>
            {enlarged && (
                <Lightbox frame={enlarged} onClose={() => setEnlarged(null)}
                    notes={events.filter((e) => frameOf.get(e.id) === frames.indexOf(enlarged)).map((e) => e.note).filter(Boolean)} />
            )}
        </div>
    );
});

export default VideoTimeline;
